import { Button, TextButton } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { usePublishSharedMenuTracking } from 'modules/menu/hooks/telemetryHooks';
import { deletePublishRequest, publishSharedMenu } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';
import { SharedMenu } from 'modules/shared-menu/types';

import ScheduleDatePicker from './ScheduleDatePicker';
import { TimeZoneNote } from './TimeZoneNote';
import { useSelectionData } from '../../../hooks/useSelectionData';
import { convertLocalIsoDateToUtcIsoDate } from '../utils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selection: SharedMenu;
};

const Root = styled(WoltModal)`
  min-width: 520px;
  border-radius: 8px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const LeftButtons = styled.div``;

const RightButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;

export const SchedulePublishModal = ({ isOpen, onClose, selection }: Props) => {
  const { formatMessage } = useIntl();
  const scope = useScopeContext();

  const existingPublishAtValue = useMemo(() => selection.draft?.publish_at, [selection]);

  const { updateSelectionData } = useSelectionData();
  const { updateSharedMenuStatus } = useSharedMenuData({ scope });

  const { mutate, isLoading, error, reset } = useMutation<unknown, Error, string>(
    async (value: string) => {
      return publishSharedMenu(selection.id, convertLocalIsoDateToUtcIsoDate(value));
    },
    {
      onSuccess: (_, publishAt) => {
        const updatedSelection = {
          ...selection,
          draft: {
            ...selection.draft,
            publish_at: convertLocalIsoDateToUtcIsoDate(publishAt),
          },
        };

        updateSelectionData({
          actionType: 'updateSelection',
          payload: updatedSelection,
        });

        updateSharedMenuStatus(selection.id, updatedSelection.draft);
        if (!!updatedSelection.draft?.publish_at) {
          toast.success(<FormattedMessage id="shared-menu.publish.scheduled.success" />);
        } else {
          toast.success(<FormattedMessage id="shared-menu.publish.scheduled.remove-success" />);
        }
        handleClose();
      },
    },
  );

  const {
    mutate: executeRemoveScheduling,
    isLoading: removingScheduling,
    error: removeScheduleError,
    reset: resetRemoveSchedule,
  } = useMutation<unknown, Error>(() => deletePublishRequest(selection.id), {
    onSuccess: () => {
      const updatedSelection = {
        ...selection,
        draft: {
          ...selection.draft,
          publish_at: undefined,
        },
      };

      updateSelectionData({
        actionType: 'updateSelection',
        payload: updatedSelection,
      });

      updateSharedMenuStatus(selection.id, updatedSelection.draft);
      toast.success(<FormattedMessage id="shared-menu.publish.scheduled.remove-success" />);
      handleClose();
    },
  });

  const [value, setValue] = useState<string | null>(
    !!existingPublishAtValue ? new Date(existingPublishAtValue).toISOString() : new Date().toISOString(),
  );

  const handleClose = useCallback(() => {
    reset();
    resetRemoveSchedule();
    onClose();
  }, [reset, resetRemoveSchedule, onClose]);

  const trackMenuPublishing = usePublishSharedMenuTracking(selection.id);

  return (
    <Root isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        {!!existingPublishAtValue ? (
          <FormattedMessage id="shared-menu.publish.scheduled.edit-label" />
        ) : (
          <FormattedMessage id="shared-menu.publish.scheduled.label" />
        )}
      </ModalHeader>
      <ModalContent style={{ padding: 0 }}>
        <FormRow horizontalPadding label={formatMessage({ id: 'shared-menu.publish.scheduled.publish-time' })}>
          <ScheduleDatePicker displayUtcHint value={value} onChange={val => setValue(val)} />
          <TimeZoneNote />
        </FormRow>
      </ModalContent>
      <StyledFooter>
        {(error || removeScheduleError) && (
          <APIErrorNotification
            style={{ width: '100%', marginBlockEnd: '16px' }}
            error={error ?? removeScheduleError}
          />
        )}
        <Buttons>
          <LeftButtons>
            {!!existingPublishAtValue && (
              <TextButton
                variant="red"
                disabled={isLoading || removingScheduling}
                onClick={() => executeRemoveScheduling()}
              >
                <FormattedMessage id="shared-menu.publish.scheduled.remove-schedule" />
              </TextButton>
            )}
          </LeftButtons>
          <RightButtons>
            <CancelButton onClick={onClose} disabled={isLoading}>
              <FormattedMessage id="state.cancel" />
            </CancelButton>
            <ConfirmButton
              type="submit"
              onClick={() => {
                // The button is disabled if value is null, but we have to satisfy TS
                if (value) {
                  mutate(value);
                  trackMenuPublishing(new Date(value));
                }
              }}
              disabled={isLoading || !value || removingScheduling}
              loading={isLoading || removingScheduling}
            >
              <FormattedMessage id="shared-menu.publish.scheduled.label" />
            </ConfirmButton>
          </RightButtons>
        </Buttons>
      </StyledFooter>
    </Root>
  );
};
